import React, { useState } from 'react';
import { Button, Modal, SparkIcon, SparkModalPortal, Text } from '@spark';
import { formatFieldPreviewModalSubtitle } from './fieldPreviewFormat';

/**
 * Light-blue field preview card (Figma 4012:19120 / 4012:19150): `<Label>:`
 * + one sample clamped to 3 lines, eye (full sample in a modal) and shuffle
 * pinned top-right. Used for Example / Summary / Output / Label samples on
 * Data Collection and for the live Primary-input preview inside Map prompt
 * template; the trajectory mapper's `.cw-tj-card` is the same card.
 */
const FieldPreviewCard = ({
    label,
    text,
    values,
    rowIndex = 0,
    rowTotal,
    columnName,
    onShuffle,
    shuffleAriaLabel,
    showShuffle = true,
    showPreview = true,
}) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    if (!text) return null;

    const samples = values || [];
    const total = rowTotal ?? samples.length;
    // "Record number N · from column X" — the record caption lives in the
    // eye modal only; the card itself is `<Label>:` + sample (Figma 4012:19120).
    const modalSubtitle = formatFieldPreviewModalSubtitle(rowIndex, total, columnName || label);
    const shuffleDisabled = samples.length < 2;
    const shuffleLabel = shuffleAriaLabel || `Shuffle ${label}`;
    const previewLabel = `Preview ${label}`;
    const closePreview = () => setPreviewOpen(false);

    return (
        <>
            <div className="cw-dc-example-preview">
                <div className="cw-dc-example-preview__body">
                    <Text
                        variant="base-medium"
                        color="primary"
                        as="p"
                        className="cw-dc-example-preview__title"
                    >
                        {`${label}:`}
                    </Text>
                    <Text
                        variant="detail-regular"
                        color="primary"
                        as="p"
                        className="cw-dc-example-preview__text"
                    >
                        {text}
                    </Text>
                </div>
                <div className="cw-dc-example-preview__actions">
                    {/* Figma Button Icon on the card is the ghost variant. */}
                    {showPreview && (
                        <Button
                            size="tiny"
                            variant="ghost"
                            icon={<SparkIcon slug="eye" size="small" />}
                            iconSlug="eye"
                            aria-label={previewLabel}
                            tooltip={previewLabel}
                            onClick={() => setPreviewOpen(true)}
                        />
                    )}
                    {showShuffle && (
                        <Button
                            size="tiny"
                            variant="ghost"
                            icon={<SparkIcon slug="arrows-shuffle" size="small" />}
                            iconSlug="arrows-shuffle"
                            aria-label={shuffleLabel}
                            tooltip={`Show a different ${String(label).toLowerCase()}`}
                            disabled={shuffleDisabled}
                            onClick={onShuffle}
                        />
                    )}
                </div>
            </div>
            {previewOpen ? (
                <SparkModalPortal
                    open
                    onClose={closePreview}
                    size="slot"
                    destroyOnClose
                    verticalAlign="top"
                    className="cw-dc-field-preview-modal-portal"
                >
                    <Modal
                        title={label}
                        subtitle={modalSubtitle}
                        showSubtitle={Boolean(modalSubtitle)}
                        showOverlay={false}
                        showScrollBar
                        fixedHeight={false}
                        scrollResetKey={rowIndex}
                        className="cw-dc-field-preview-modal"
                        tertiaryLabel="Shuffle"
                        tertiaryIcon={<SparkIcon slug="arrows-shuffle" size="medium" />}
                        tertiaryDisabled={shuffleDisabled}
                        showTertiaryButton={showShuffle}
                        primaryLabel="Close"
                        onTertiaryClick={onShuffle}
                        onPrimaryClick={closePreview}
                        onCloseClick={closePreview}
                    >
                        <div className="cw-dc-field-preview-modal__body">
                            <Text variant="detail-medium" color="secondary" as="p">
                                {label}
                            </Text>
                            <div className="cw-dc-field-preview-modal__box">
                                <Text variant="base-regular" color="primary" as="p">
                                    {text}
                                </Text>
                            </div>
                        </div>
                    </Modal>
                </SparkModalPortal>
            ) : null}
        </>
    );
};

export default FieldPreviewCard;
